// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { Link, useParams } from '@tanstack/react-router';

// material ui icons
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

// material ui
import { Badge, Box, Button, Grow, InputAdornment } from '@mui/material';
import {
  GridToolbar,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-premium';

// shared components
import PresetSelector from '@/components/ui/datagrid/PresetSelector';

const SubscriptionDataGridToolbar = ({ onSubscriptionCreate, ...props }) => {
  const { t } = useTranslation();
  const [showToolbar, setShowToolbar] = useState(false);
  const { buildingName } = useParams({ strict: false });
  return (
    <GridToolbarContainer
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1,
        background: 'inherit',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <GridToolbarQuickFilter
          size="small"
          sx={{
            '& .MuiInputBase-root': {
              backgroundColor: (theme) => theme.palette.background.paper,
              borderRadius: 2,
            },
            '& .MuiFilledInput-root': {
              borderRadius: '5px',
            },
            '& .MuiInput-underline:before, & .MuiInput-underline:after': {
              display: 'none',
            },
            '& .MuiInputLabel-root': {
              color: 'gray',
            },
          }}
          variant="outlined"
          InputProps={{
            placeholder: 'Søg subscriptions...',
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ color: 'gray' }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          startIcon={<HomeWorkRoundedIcon />}
          endIcon={<ArrowForwardRoundedIcon />}
          variant="outlined"
          component={Link}
          to=".."
          sx={{
            mx: 1,
            borderRadius: 2,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            textTransform: 'none',
            p: 0.9,
            mt: -0.5,
            color: 'text.primary',
            ':hover': {
              borderColor: 'divider',
            },
          }}
        >
          {t('actions.goToItem', {
            item: t('items.building.indefinitePlural', { ns: 'glossary' }),
          })}
        </Button>
        <PresetSelector {...props} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <>
          <Grow in={showToolbar}>
            <Box>
              <GridToolbar />
            </Box>
          </Grow>

          <Badge
            color="primary"
            badgeContent={props.filterModel?.items?.length || 0}
            invisible={!(props.filterModel?.items?.length > 0)} // hide badge when no filters
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              badge: {
                sx: {
                  // Pull the badge INSIDE the button so it won't be clipped
                  top: -7,
                  right: -7,
                  transform: 'none', // cancel default translate(50%, -50%)
                  zIndex: 1,
                },
              },
            }}
          >
            <Button
              startIcon={
                showToolbar ? <FilterListOffIcon /> : <FilterListIcon />
              }
              variant="outlined"
              size="small"
              onClick={() => setShowToolbar(!showToolbar)}
              sx={{
                borderRadius: 2,
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                textTransform: 'none',
                p: 0.9,
                mt: -0.5,
                color: 'text.primary',
              }}
            >
              {showToolbar ? 'Skjul indstillinger' : 'Vis indstillinger'}
            </Button>
          </Badge>
        </>

        {buildingName && (
          <Button
            startIcon={<AddRoundedIcon />}
            variant="contained"
            onClick={onSubscriptionCreate}
            sx={{ mt: -0.5 }}
          >
            {t('actions.createItem', {
              item: t('items.subscription.indefinite', { ns: 'glossary' }),
            })}
          </Button>
        )}
      </Box>
    </GridToolbarContainer>
  );
};

export default SubscriptionDataGridToolbar;
